import React, { useState, useEffect, useCallback } from 'react';
import SocialActions from '../../components/SocialActions/SocialActions';
import ContentEditorModal from '../../components/ContentEditorModal/ContentEditorModal';
import { useAuth } from '../../contexts/AuthContext';

const WudasieMariam = () => {
  const { user } = useAuth();
  const [hymns, setHymns] = useState([]);
  const [filteredHymns, setFilteredHymns] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal State
  const [editorOpen, setEditorOpen] = useState(false);
  const [isNewContent, setIsNewContent] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const apiURL = process.env.REACT_APP_API_URL || '/api';

  const loadHymnsFromDB = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiURL}/content?type=wudasie`);
      if (res.ok) {
        const json = await res.json();
        if (json.success && Array.isArray(json.data) && json.data.length > 0) {
          const mapped = json.data.map(item => ({
            id: item.itemId || item._id,
            _id: item._id,
            title: item.title,
            content: Array.isArray(item.content)
              ? (item.content[0]?.main ? item.content.map(c => c.main || c.title) : item.content)
              : [item.content]
          }));
          setHymns(mapped);
          setFilteredHymns(mapped);
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.error('API fetch error for Wudasie:', err.message);
    }
    setHymns([]);
    setFilteredHymns([]);
    setLoading(false);
  }, [apiURL]);

  useEffect(() => {
    loadHymnsFromDB();
  }, [loadHymnsFromDB]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredHymns(hymns);
      setCurrentIndex(0);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = hymns.map(hymn => {
        const content = hymn.content || [];
        const matches = Array.isArray(content) ? content
          .filter(line => typeof line === 'string' && line.toLowerCase().includes(query)) : [];
        const titleMatch = hymn.title?.toLowerCase().includes(query);
        if (titleMatch || matches.length > 0) {
          return { ...hymn, snippets: matches.slice(0, 3) };
        }
        return null;
      }).filter(hymn => hymn !== null);
      setFilteredHymns(filtered);
      setCurrentIndex(0);
    }
  }, [searchQuery, hymns]);

  const handleNext = () => {
    if (filteredHymns.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % filteredHymns.length);
  };

  const handlePrev = () => {
    if (filteredHymns.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + filteredHymns.length) % filteredHymns.length);
  };

  const handleTitleClick = (index) => {
    setCurrentIndex(index);
    setSearchQuery('');
  };

  const handleSearchResultClick = (index) => {
    setCurrentIndex(index);
    setSearchQuery('');
  };

  const currentHymn = filteredHymns[currentIndex] || null;
  const isSearchMode = searchQuery.trim().length > 0;
  const canEditContent = user?.canEdit || user?.canUpdate || user?.isAdmin;
  const canInsertContent = user?.canInsert || user?.isAdmin;

  return (
    <div className="page-container wudase-theme max-w-7xl mx-auto p-4 sm:p-6">
      <div className="page-header text-center mb-6">
        <h1 className="page-main-title text-3xl font-extrabold text-blue-900 mb-2">🙏 ውዳሴ ማርያም</h1>
        <div className="page-subtitle text-gray-600">Praise of Mary - የሰባቱ ቀናት ጸሎት</div>
        <div className="mt-2 flex items-center justify-center gap-2">
          <span className="text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 font-medium">
            ⚡ ከመረጃ ቋት (MongoDB) የተገኘ
          </span>
          {canEditContent && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium flex items-center gap-1">
              <span>👤</span> የተፈቀደ አርታዒ ({user?.email})
            </span>
          )}
        </div>
      </div>

      <div className="controls-container bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6 border border-gray-200">
        <div className="controls-grid">
          <div className="control-group">
            <label className="control-label text-xs font-bold text-gray-700 mb-1">🔍 ፈልግ (Search)</label>
            <input
              type="text"
              placeholder="በውዳሴ ማርያም ውስጥ ቃል ይፈልጉ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        {canInsertContent && (
          <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
            <button
              onClick={() => {
                setIsNewContent(true);
                setEditingItem(null);
                setEditorOpen(true);
              }}
              className="px-3.5 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-xs font-semibold rounded-lg shadow transition flex items-center gap-1.5"
            >
              <span>➕</span> አዲስ የውዳሴ ክፍል ወደ DB አስገባ
            </button>
          </div>
        )}
      </div>

      {!isSearchMode && hymns.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          {hymns.map((h, i) => (
            <button
              key={h.id || i}
              onClick={() => handleTitleClick(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${currentIndex === i ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {h.title}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">ውዳሴ ማርያም ከመረጃ ቋት እየተጫነ ነው...</div>
      ) : isSearchMode ? (
        <div className="bg-white rounded-xl shadow p-4 border border-gray-200">
          <h2 className="text-base font-bold mb-3">የፍለጋ ውጤቶች ({filteredHymns.length})</h2>
          {filteredHymns.length > 0 ? (
            <div className="space-y-2">
              {filteredHymns.map((hymn, index) => (
                <div
                  key={hymn.id}
                  className="p-3 border rounded-lg hover:bg-blue-50 cursor-pointer transition text-sm flex items-center justify-between"
                  onClick={() => handleSearchResultClick(index)}
                >
                  <span className="font-bold text-blue-700">{hymn.title}</span>
                  <span className="text-xs text-blue-500">ይመልከቱ →</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">ምንም ውጤት አልተገኘም</p>
          )}
        </div>
      ) : currentHymn ? (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 space-y-4">
          <div className="border-b border-gray-200 pb-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <h2 className="text-2xl font-bold text-gray-800">{currentHymn.title}</h2>
            {canEditContent && (
              <button
                onClick={() => {
                  setIsNewContent(false);
                  setEditingItem({
                    ...currentHymn,
                    content: currentHymn.content?.map(line => ({ title: '', main: line }))
                  });
                  setEditorOpen(true);
                }}
                className="px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold shadow transition flex items-center gap-1.5"
              >
                <span>✏️</span> ይህን ክፍል አርትዕ (Edit in DB)
              </button>
            )}
          </div>

          <div className="space-y-3 leading-relaxed text-base text-gray-800">
            {Array.isArray(currentHymn.content) && currentHymn.content.map((line, i) => (
              <p key={i} className="hymn-line border-b border-gray-50 pb-1">
                {typeof line === 'string' ? line : (line?.main || line?.title || JSON.stringify(line))}
              </p>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <SocialActions
              contentId={`wudasie_${currentHymn.id}`}
              contentType="wudasie"
              title={currentHymn.title}
              content={Array.isArray(currentHymn.content) ? currentHymn.content.join(' ').substring(0, 300) : ''}
              showBookmark={true}
              showShare={true}
              showLike={true}
            />
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium disabled:opacity-40 transition"
            >
              ← ቀዳሚ
            </button>
            <span className="text-xs text-gray-500 font-semibold">
              {currentIndex + 1} / {filteredHymns.length}
            </span>
            <button
              onClick={handleNext}
              disabled={currentIndex === filteredHymns.length - 1}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium disabled:opacity-40 transition"
            >
              ቀጣይ →
            </button>
          </div>
        </div>
      ) : null}

      <ContentEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        contentType="wudasie"
        initialData={editingItem}
        isNew={isNewContent}
        onSaved={() => loadHymnsFromDB()}
      />
    </div>
  );
};

export default WudasieMariam;
